/* eslint-disable react-native/no-unused-styles */

import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';
import type { AccessibilityProps, ViewStyle } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { theme } from '@garnish/constants';

import { Button } from '../Button';
import { BodyText } from '../Text';

// ─────────────────────────────────────────────────────────────────────────────

export const ButtonGroupItem = (props: ButtonGroupItemProps) => {
  const {
    value,
    setValue,
    isActive,
    label,
    accessibilityHint,
    helperText,
    gap,
    horizontalPadding,
    fluid = false,
    size,
  } = props;

  const handleOnPress = useCallback(() => {
    setValue(value);
  }, [setValue, value]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerGapStyle = gap ? containerGapStyles[gap] : undefined;
  const containerFluidStyle = fluid ? styles.containerFluid : undefined;
  const containerStyles = [containerFluidStyle, containerGapStyle];
  const horizontalPaddingStyle = horizontalPadding
    ? buttonHorizontalPaddingStyles[horizontalPadding]
    : undefined;

  const palette = isActive ? 'primaryOutline' : 'muted';

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyles}>
      <Button
        onPress={isActive ? undefined : handleOnPress}
        palette={palette}
        style={horizontalPaddingStyle}
        size={size}
        withFocusOutline={false}
        accessibilityRole="radio"
        accessibilityHint={accessibilityHint}
        accessibilityState={{ checked: isActive }}
        {...{ accessibilityChecked: isActive }}
      >
        {label}
      </Button>

      {helperText ? (
        <View style={styles.helperTextContainer}>
          <BodyText size={5} style={styles.helperText}>
            {helperText}
          </BodyText>
        </View>
      ) : null}
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerFluid: {
    flex: 1,
  },
  helperTextContainer: {
    marginTop: theme.spacing['2'],
  },
  helperText: {
    textAlign: 'center',
    color: theme.colors.CHARCOAL,
  },
});

const containerGapStyles = StyleSheet.create<Record<Spacing, ViewStyle>>({
  none: {},
  xsmall: { marginRight: theme.spacing['1'] },
  small: { marginRight: theme.spacing['2'] },
  default: { marginRight: 12 },
  medium: { marginRight: theme.spacing['4'] },
  large: { marginRight: theme.spacing['6'] },
  xlarge: { marginRight: theme.spacing['10'] },
  xxlarge: { marginRight: theme.spacing['20'] },
});

const buttonHorizontalPaddingStyles = StyleSheet.create<
  Record<Spacing, ViewStyle>
>({
  none: { paddingHorizontal: 0 },
  xsmall: { paddingHorizontal: theme.spacing['1'] },
  small: { paddingHorizontal: theme.spacing['2'] },
  default: { paddingHorizontal: 12 },
  medium: { paddingHorizontal: theme.spacing['4'] },
  large: { paddingHorizontal: theme.spacing['6'] },
  xlarge: { paddingHorizontal: theme.spacing['10'] },
  xxlarge: { paddingHorizontal: theme.spacing['20'] },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Spacing = keyof typeof theme.spacing | 'default' | 'none';

type ButtonGroupItemProps = {
  readonly value: string;
  readonly setValue: (value: string) => void;
  readonly label: string;
  readonly isActive?: boolean;
  readonly helperText?: string;
  readonly accessibilityHint?: AccessibilityProps['accessibilityHint'];
  readonly fluid?: boolean;
  readonly size?: ComponentProps<typeof Button>['size'];
  readonly horizontalPadding?: Spacing;
  readonly gap?: Spacing;
};
